/**
 * Settings Page - Tailwind Version
 *
 * Professional restaurant management view.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Storage } from "aws-amplify";
import {
  Save,
  MapPin,
  Clock,
  Image as ImageIcon,
  Mail,
  Phone,
  Store,
  Check,
  Building2,
  Trophy,
  Activity,
  RefreshCw,
} from "lucide-react";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";

import { Restaurant } from "@tasty/api";
import { RestaurantClient } from "@tasty/api/client";
import { TERMS, getAllLocations } from "@tasty/shared";
import { useRestaurantContext } from "../../context/restaurant-context";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader } from "../../components/ui/card";
import { cn, formatPrice } from "../../lib/utils";
import { toast } from "sonner";
import { loadGoogleMapsPlacesJs } from "../../lib/google-maps-loader";

type Coordinates = {
  lat: number;
  lng: number;
} | null;

export default function SettingsPage() {
  const { restaurant, setRestaurant } = useRestaurantContext();

  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [emailPrinter, setEmailPrinter] = useState("");
  const [coordinates, setCoordinates] = useState<Coordinates>(null);
  const [minDeliveryTime, setMinDeliveryTime] = useState(45);
  const [maxDeliveryTime, setMaxDeliveryTime] = useState(60);
  const [locationID, setLocationID] = useState<string>("");

  // Branding
  const [logoKey, setLogoKey] = useState<string | null>(null);
  const [teaserKey, setTeaserKey] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [teaserUrl, setTeaserUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingTeaser, setUploadingTeaser] = useState(false);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const teaserInputRef = useRef<HTMLInputElement | null>(null);

  const {
    ready,
    value: address,
    setValue: setAddress,
    suggestions: { status, data },
    clearSuggestions,
    init,
  } = usePlacesAutocomplete({ initOnMount: false });

  // Load Google Maps Places library (Vite)
  useEffect(() => {
    let cancelled = false;
    const apiKey = typeof import.meta.env.VITE_GOOGLE_MAPS_API_KEY === "string" ? import.meta.env.VITE_GOOGLE_MAPS_API_KEY : "";
    if (!apiKey) return;
    loadGoogleMapsPlacesJs({ apiKey }).then(() => { if (!cancelled) init(); });
    return () => { cancelled = true; };
  }, [init]);

  // Load Initial Data
  useEffect(() => {
    if (restaurant) {
      setName(restaurant.name || "");
      setPhone(restaurant.phone || "");
      setEmail(restaurant.email || "");
      setEmailPrinter(restaurant.emailPrinter || "");
      setAddress(restaurant.address || "", false);
      setCoordinates({ lat: restaurant.lat, lng: restaurant.lng });
      setMinDeliveryTime(restaurant.minDeliveryTime || 45);
      setMaxDeliveryTime(restaurant.maxDeliveryTime || 60);
      setLocationID(restaurant.locationID || "");
      setLogoKey(restaurant.image?.logoKey ?? null);
      setTeaserKey(restaurant.image?.teaserKey ?? null);
    }
  }, [restaurant, setAddress]);

  // Resolve Images
  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      const keys = [logoKey?.trim(), teaserKey?.trim()];
      const setters = [setLogoUrl, setTeaserUrl];
      
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (!key) setters[i](null);
        else {
          try {
            const url = await Storage.get(key, { level: "public", track: true, expires: 3600 });
            if (!cancelled) setters[i](String(url));
          } catch { if (!cancelled) setters[i](null); }
        }
      }
    };
    resolve();
    return () => { cancelled = true; };
  }, [logoKey, teaserKey]);

  const selectLocation = async (description: string) => {
    setAddress(description, false);
    clearSuggestions();
    try {
      const results = await getGeocode({ address: description });
      const { lat, lng } = await getLatLng(results[0]);
      setCoordinates({ lat, lng });
    } catch (error) { console.error("Geocoding error:", error); }
  };

  const handleSave = async () => {
    if (!restaurant?.id) return;
    setLoading(true);
    try {
      const updated = await RestaurantClient.updateItem({
        id: restaurant.id,
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim().toLowerCase(),
        emailPrinter: emailPrinter.trim().toLowerCase(),
        address: address.trim(),
        lat: coordinates?.lat || restaurant.lat,
        lng: coordinates?.lng || restaurant.lng,
        minDeliveryTime,
        maxDeliveryTime,
        image: { logoKey: logoKey || null, teaserKey: teaserKey || null },
        locationID: locationID || null,
      });
      if (updated) {
        setRestaurant(updated);
        toast.success("Einstellungen gespeichert");
      }
    } catch (error) { toast.error("Fehler beim Speichern"); } finally { setLoading(false); }
  };

  const uploadBrandingImage = async (kind: "logo" | "teaser", file: File) => {
    const setter = kind === "logo" ? setUploadingLogo : setUploadingTeaser;
    setter(true);
    try {
      const key = `branding/${restaurant?.id}/${kind}_${Date.now()}.${file.name.split(".").pop()}`;
      await Storage.put(key, file, { level: "public", contentType: file.type });
      kind === "logo" ? setLogoKey(key) : setTeaserKey(key);
      toast.success(`${kind === "logo" ? "Logo" : "Titelbild"} hochgeladen`);
    } catch (error) { toast.error("Upload fehlgeschlagen"); } finally { setter(false); }
  };

  return (
    <div className="w-full space-y-10 animate-in fade-in duration-500">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b pb-6">
        <div className="space-y-1">
          <h1 className="text-3xl font-black tracking-tighter">Settings</h1>
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest opacity-60">Manage your business profile</p>
        </div>
        <Button onClick={handleSave} disabled={loading} className="h-10 px-8 rounded-xl font-bold shadow-lg">
          {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Speichern
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Left Column: Forms */}
        <div className="lg:col-span-8 space-y-10">
          <section className="space-y-4">
            <div className="flex items-center gap-2 ml-1">
              <Building2 className="h-4 w-4 text-primary" />
              <h3 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">General Information</h3>
            </div>
            <Card className="rounded-2xl border-muted-foreground/10 bg-background shadow-sm overflow-hidden">
              <CardContent className="p-8 space-y-8">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest ml-1">{TERMS.businessName}</label>
                  <Input value={name} onChange={e => setName(e.target.value)} className="h-12 rounded-xl bg-muted/5 border-muted-foreground/10 font-bold text-base" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest ml-1">Phone Number</label>
                    <Input value={phone} onChange={e => setPhone(e.target.value)} className="h-12 rounded-xl bg-muted/5 border-muted-foreground/10 font-bold" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest ml-1">Business Email</label>
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)} className="h-12 rounded-xl bg-muted/5 border-muted-foreground/10 font-bold" />
                  </div>
                </div>
                <div className="space-y-2 pt-4 border-t border-dashed border-muted-foreground/10">
                  <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest ml-1">Printer Email (Auto-Orders)</label>
                  <Input type="email" value={emailPrinter} onChange={e => setEmailPrinter(e.target.value)} className="h-12 rounded-xl bg-muted/5 border-muted-foreground/10 font-bold" />
                </div>
              </CardContent>
            </Card>
          </section>

          <section className="space-y-4">
            <div className="flex items-center gap-2 ml-1">
              <MapPin className="h-4 w-4 text-primary" />
              <h3 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Location Details</h3>
            </div>
            <Card className="rounded-2xl border-muted-foreground/10 bg-background shadow-sm overflow-hidden">
              <CardContent className="p-8 space-y-8">
                <div className="relative space-y-2">
                  <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest ml-1">Business Address</label>
                  <Input value={address} onChange={e => setAddress(e.target.value)} disabled={!ready} className="h-12 rounded-xl bg-muted/5 border-muted-foreground/10 font-bold" />
                  {status === "OK" && data.length > 0 && (
                    <div className="absolute z-50 w-full mt-2 bg-background border border-muted-foreground/10 rounded-2xl shadow-2xl overflow-hidden py-2">
                      {data.map(({ place_id, description }) => (
                        <button key={place_id} className="w-full px-6 py-3 text-left hover:bg-muted text-sm font-bold transition-colors" onClick={() => selectLocation(description)}>{description}</button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest ml-1">Stadt</label>
                  <select
                    value={locationID}
                    onChange={(e) => setLocationID(e.target.value)}
                    className="h-12 w-full rounded-xl bg-muted/5 border border-muted-foreground/10 font-bold px-4 text-sm"
                  >
                    <option value="">Bitte wählen</option>
                    {getAllLocations().map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="h-64 bg-muted/10 rounded-2xl border-2 border-dashed border-muted-foreground/10 flex flex-col items-center justify-center group hover:bg-muted/20 transition-all">
                  <MapPin className="h-10 w-10 text-muted-foreground/20 mb-3 group-hover:scale-110 transition-transform" />
                  <p className="text-[10px] font-black text-muted-foreground/40 uppercase tracking-widest text-center px-8 leading-relaxed">Map integration will display your location accurately to customers.</p>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>

        {/* Right Column: Stats & Branding */}
        <div className="lg:col-span-4 space-y-10">
          <section className="space-y-4">
            <div className="flex items-center gap-2 ml-1">
              <Activity className="h-4 w-4 text-primary" />
              <h3 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Operations</h3>
            </div>
            <Card className="rounded-2xl border-muted-foreground/10 bg-background shadow-sm overflow-hidden">
              <CardContent className="p-8 space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/60">Min Prep (Min)</label>
                    <Input type="number" value={minDeliveryTime} onChange={e => setMinDeliveryTime(Number(e.target.value))} className="h-12 rounded-xl font-black text-center text-lg bg-muted/5 border-muted-foreground/10" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/60">Max Prep (Min)</label>
                    <Input type="number" value={maxDeliveryTime} onChange={e => setMaxDeliveryTime(Number(e.target.value))} className="h-12 rounded-xl font-black text-center text-lg bg-muted/5 border-muted-foreground/10" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          <section className="space-y-4">
            <div className="flex items-center gap-2 ml-1">
              <Trophy className="h-4 w-4 text-primary" />
              <h3 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Identity</h3>
            </div>
            <Card className="rounded-2xl border-muted-foreground/10 bg-background shadow-sm overflow-hidden">
              <CardContent className="p-8 space-y-10">
                <div className="space-y-4">
                  <div className="flex items-center justify-between px-1">
                    <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/70">Logo</label>
                    <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} className="h-7 text-[9px] font-black uppercase tracking-widest border-muted-foreground/20 rounded-lg">Update</Button>
                  </div>
                  <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if(f) uploadBrandingImage("logo", f); }} />
                  <div className="aspect-square rounded-2xl border border-muted-foreground/10 flex items-center justify-center bg-muted/5 overflow-hidden p-6 group hover:border-primary/20 transition-all shadow-inner">
                    {logoUrl ? <img src={logoUrl} alt="" className="h-full w-full object-contain group-hover:scale-105 transition-transform" /> : <p className="text-[9px] font-black uppercase text-muted-foreground/30">Logo</p>}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between px-1">
                    <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/70">Cover</label>
                    <Button variant="outline" size="sm" onClick={() => teaserInputRef.current?.click()} className="h-7 text-[9px] font-black uppercase tracking-widest border-muted-foreground/20 rounded-lg">Update</Button>
                  </div>
                  <input ref={teaserInputRef} type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if(f) uploadBrandingImage("teaser", f); }} />
                  <div className="aspect-video rounded-2xl border border-muted-foreground/10 flex items-center justify-center bg-muted/5 overflow-hidden group hover:border-primary/20 transition-all shadow-inner">
                    {teaserUrl ? <img src={teaserUrl} alt="" className="h-full w-full object-cover group-hover:scale-105 transition-transform" /> : <p className="text-[9px] font-black uppercase text-muted-foreground/30">Cover</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </div>
    </div>
  );
}
